import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchReportList,
  fetchReportById,
  createNewReport,
  addNewReport,
  deleteReport,
  generatePDF,
  renameReport,
  mergeReports,
  fetchMergeableReports,
  createBatchReport,
  generateSummaryRequest,
  updateSummaryRequest,
  getSummaryRequest,
} from "~/services/gateways/report.gateway";
import { docxGateway } from "_@snap/reports/services/gateways/docx.gateway";
import { SummaryOutputDTO } from "root/domain/http/dto/report/summary.output";
import { base64ToUint8Array, useEncryption } from "~/hooks/useEncryption";
import type {
  EncryptedPayload,
  ReportData,
  ReportPDFProps,
  NewReportRequest,
  MergeReportsPayload,
  MergeReportsProps,
  BatchReportRequest,
  MergeableReportItem,
  SummaryResponse,
} from "~/types/global";
import { FolderData } from "root/domain/entities/folder.model";
import { toast } from "sonner";
import { useDialogActions } from "~/store/dialogStore";
import { useNewPendingReportsActions } from "~/store/newReportStatusStore";
import {
  decryptReportPayload,
  encryptReportPayload,
  combineReportSearchArgs,
  normalizeDocument,
} from "~/helpers";
import { REPORT_CONSTANTS, tanstackQueryConfig } from "~/helpers/constants";
import {
  useReportListLoadCount,
  useReportListPage,
  useReportListActions,
  useReportListSearchFilter,
} from "~/store/reportListStore";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { decryptListItem } from "~/helpers/item-decryption.helper";
import { useIsPasswordExpired } from "~/store/credentials";
import { usePasswordCheck } from "~/hooks/usePasswordCheck";
import { usePassword } from "~/store/credentials";
import { useUserActions } from "~/store/userStore";
import { type UpdateSummaryInput } from "root/domain/http/dto/report/summary.input";
import { usePermissionManager } from "~/services/permissionManager";
import { getUserAiModel } from "~/store/userStore";
import { useOrgPublicKey } from "~/hooks/useOrgEncryption";
import {
  generatePerReportDek,
  wrapDekForUser,
  bufferToBase64,
  unwrapDekFromUser
} from "~/helpers/envelopeCrypto";
import { importRsaPublicKey, rsaOaepWrapDek } from "~/helpers/orgCrypto";

import { useEnvelopeAwareDecryptor } from "~/hooks/useEnvelopeAwareDecryptor";
import { useIsMigrationPending } from "~/store/legacyMigrationStore";

export const useReportCRUD = (folderId?: string | null, enableQuery: boolean = true) => {
  const { closeDialog } = useDialogActions();
  const queryClient = useQueryClient();
  const {
    decryptData,
    _encryptData,
    encryptNgrams,
    encryptPayloadDeterministic,
    deriveKey,
  } = useEncryption();
  const { canViewReportSummary, canCreateReportSummary } = usePermissionManager();
  const { data: orgPublicKey } = useOrgPublicKey();
  const { clearPendingList } = useNewPendingReportsActions();
  const pageNumber = useReportListPage();
  const limitNumber = useReportListLoadCount();
  const searchFilter = useReportListSearchFilter();
  const { setReportList, incrementReportList, resetPagination, setLoadMore, clearSearchFilter, updateItemById } =
    useReportListActions();
  const secretKey = usePassword();
  const isPasswordExpired = useIsPasswordExpired();
  const { withPasswordCheck } = usePasswordCheck();
  const { setUserCredits } = useUserActions();

  const { createDecryptorForItem: createEnvelopeAwareDecryptor } = useEnvelopeAwareDecryptor();
  const isMigrationPending = useIsMigrationPending();

  const reportListQuery = useQuery({
    queryKey: ["reports", "list", folderId],
    queryFn: async ({ meta }) => {
      try {
        clearPendingList();

        let hmacFilters;
        if (searchFilter) {
          console.log("reportListQuery searchFilter", searchFilter);
          const ngrams = await encryptNgrams({
            searched_value: searchFilter,
          });
          hmacFilters = ngrams.searched_value;
          //clearSearchFilter()
        }

        const reportsListData = await fetchReportList({
          page: pageNumber,
          limit: limitNumber,
          hmacFilters,
          folderId: folderId,
        });

        const decryptedItemsList = await Promise.all(
          reportsListData.map(async (item, index) => {
            try {
              const itemDecryptor = createEnvelopeAwareDecryptor(item as any);
              return await decryptListItem(item, itemDecryptor, createEnvelopeAwareDecryptor, deriveKey);
            } catch (error) {
              console.error(`Error decrypting item #${index + 1}:`, error);
              // Return item as-is if decryption fails (e.g. folder encrypted with old key after password reset)
              return item;
            }
          })
        );

        const finalList = decryptedItemsList as ReportData[];

        //@ts-ignore
        pageNumber === 1 ? setReportList(finalList) : incrementReportList(finalList);
        const itemCount = finalList.length;
        const hasMore = Boolean(itemCount === limitNumber);
        setLoadMore(hasMore);
        console.log("decrypted items list", finalList);
        // TODO - remover caso não esteja agregando NADA - teste para evitar mensagem de refetch sempre que chamar a lista
        const isScheduledRefetch = meta?.fetchReason === "refetch";
        if (isScheduledRefetch) {
          sessionStorage.removeItem("reports_list_toast_shown");
        }

        return {
          data: finalList as (ReportData | FolderData)[],
          isScheduledRefetch: isScheduledRefetch,
        };
      } catch (err: any) {
        createErrorHandler(
          "Ocorreu um erro ao tentar atualizar a lista de relatórios",
          "Erro ao atualizar lista"
        );

        return {
          data: [],
          isScheduledRefetch: false,
        };
      }
    },
    ...tanstackQueryConfig,
    staleTime: Infinity,
    enabled: !isPasswordExpired && enableQuery && folderId !== undefined && !isMigrationPending,
    meta: {
      fetchReason: "initial",
    },
  });

  const reportDetailsQuery = (id: string) =>
    useQuery<ReportData, Error>({
      queryKey: ["reports", "details", id],
      queryFn: async () => {
        const response = await fetchReportById(id);
        const reportDecryptor = createEnvelopeAwareDecryptor(response as any);
        const decrypted = await decryptReportPayload(response as ReportData, reportDecryptor);
        console.log("[useReportCRUD] reportDetailsQuery decrypted", decrypted);
        return decrypted as ReportData;
      },
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: !!id && !isPasswordExpired,
    });

  const mergeableReportsQuery = (
    reportId: string,
    page: number,
    limit: number,
    searchFilter?: string
  ) => {
    return useQuery({
      queryKey: ["reports", "mergeable", reportId, page, limit, searchFilter],
      queryFn: async () => {
        try {
          let hmacFilters;
          if (searchFilter) {
            console.log("mergeableReportsQuery searchFilter", searchFilter);
            const ngrams = await encryptNgrams({
              searched_value: searchFilter,
            });
            hmacFilters = ngrams.searched_value;
          }

          const response = await fetchMergeableReports({
            report_id: reportId,
            hmacFilters,
            page,
            limit,
          });

          const decryptedMergeableReportsList = await Promise.all(
            response.getReports().map(async (item, index) => {
              try {
                const itemDecryptor = createEnvelopeAwareDecryptor(item as any);
                const decryptedItem = (await decryptListItem(
                  item,
                  itemDecryptor,
                  createEnvelopeAwareDecryptor,
                )) as MergeableReportItem;

                if (
                  decryptedItem.related_documents_encrypted &&
                  Array.isArray(decryptedItem.related_documents_encrypted)
                ) {
                  const decryptedDocuments = await Promise.all(
                    decryptedItem.related_documents_encrypted.map(async (encDoc: any) => {
                      if (typeof encDoc === "object" && "encrypted" in encDoc && "iv" in encDoc) {
                        const decrypted = await decryptData({
                          encrypted: encDoc.encrypted,
                          iv: encDoc.iv,
                        });
                        return decrypted.success ? decrypted.data : null;
                      }
                      return encDoc;
                    })
                  );
                  decryptedItem.related_documents_encrypted = decryptedDocuments.filter(Boolean);
                }

                return decryptedItem;
              } catch (error) {
                console.error(`Error decrypting item #${index + 1}:`, error);
                throw error;
              }
            })
          );

          return {
            data: decryptedMergeableReportsList,
            pagination: response.getPagination(),
          };

          // // mock
          // const mockResponse = await fetch("/mock_lista_reports_combinaveis.json");
          // const allMockData = await mockResponse.json();
          // const startIndex = (page - 1) * limit;
          // const endIndex = startIndex + limit;
          // const paginatedData = allMockData.data.slice(startIndex, endIndex);
          // const mockPaginatedResponse = {
          //   data: paginatedData,
          //   pagination: {
          //     ...allMockData.pagination,
          //     current_page: page,
          //     page_size: limit,
          //     has_next: endIndex < allMockData.data.length,
          //     has_previous: page > 1,
          //   },
          // };

          // const mockDTO = new MergeableReportsOutputDTO(mockPaginatedResponse);

          // return {
          //   data: mockPaginatedResponse.data,
          //   pagination: mockDTO.getPagination(),
          // };
        } catch (err: any) {
          console.error("Error fetching mergeable reports:", err);
          throw err;
        }
      },
      ...tanstackQueryConfig,
      staleTime: Infinity,
      enabled: !!reportId && !isPasswordExpired,
    });
  };

  const newReportMutation = useMutation({
    mutationFn: async (report: NewReportRequest) => {
      try {
        const ngrams = await encryptNgrams({
          report_search_args: report.report_search_args,
        });

        const { report_search_args, ...newReport } = report;

        const payload = {
          ...newReport,
          [REPORT_CONSTANTS.new_report.hmac]: {
            [REPORT_CONSTANTS.new_report.ngrams]: ngrams,
          },
        };

        return createNewReport(payload);
      } catch (error) {
        console.error("Error creating new report in SNAP API:", error);
        throw new Error("Failed to create new report in SNAP API");
      }
    },
    onSuccess: async (data: { id: string; credits_minimun: number }, variables) => {
      closeDialog();
      setUserCredits(data?.credits_minimun);
      toast.success("Gerando novo relatório...");
      window.dispatchEvent(new Event("snap:report-created"));
      withPasswordCheck(() => {
        const targetFolder = variables.parent_folder_id ?? null;
        const currentFolder = folderId ?? null;
        invalidateCurrentFolderNoFilters(targetFolder);
        if (targetFolder !== currentFolder) {
          invalidateCurrentFolderNoFilters(currentFolder);
        }
      });
    },
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar criar um novo relatório",
      "Erro ao criar relatório"
    ),
  });

  const addNewReportMutation = useMutation({
    mutationFn: async (report: any) => {
      try {
        const { user_reports_id } = report;
        if (user_reports_id) {
          const uniquePNames = report?.subject_multiple_p_names?.length
            ? [...new Set(report.subject_multiple_p_names)]
            : undefined;
          const uniqueCNames = report?.subject_multiple_c_names?.length
            ? [...new Set(report.subject_multiple_c_names)]
            : undefined;

          const ngrams = await encryptNgrams({
            report_name: report.report_name,
            report_search_args: report.report_search_args,
            subject_name: report.subject_name,
            subject_mother_name: report.subject_mother_name,
            ...(report?.subject_name_related && {
              subject_name_related: report.subject_name_related,
            }),
            ...(uniquePNames && {
              subject_multiple_p_names: uniquePNames,
            }),
            ...(uniqueCNames && {
              subject_multiple_c_names: uniqueCNames,
            }),
          });
          console.log("[ useReportCRUD ] report", report);

          /* não enviar mais created_at, modified_at, related_documents */
          const { created_at, modified_at, related_documents, ...newReport } = report;
          const modifiedReports = {
            ...newReport,
            [REPORT_CONSTANTS.new_report.report_status]: REPORT_CONSTANTS.status.completed,
          };

          let encryptedReport: ReportData;
          let envelopeFields: {
            user_wrapped_dek: string | null;
            user_dek_iv: string | null;
            org_wrapped_dek: string | null;
            key_version: number | null;
          } = { user_wrapped_dek: null, user_dek_iv: null, org_wrapped_dek: null, key_version: null };

          // Org users: encrypt fields with per-report DEK, wrap DEK for both user and org paths
          // Standalone users: encrypt fields directly with user's key (legacy path, unchanged)
          if (orgPublicKey) {
            const perReportDek = generatePerReportDek();

            // Encrypt all fields with the per-report DEK
            encryptedReport = await encryptReportPayload(
              modifiedReports,
              null,
              async (data: any, _ignored: ArrayBuffer) => _encryptData(data, perReportDek.buffer as ArrayBuffer),
            );

            // Wrap per-report DEK with user's key (user path)
            const userDek = base64ToUint8Array(secretKey || "");
            const rawUserKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
            const wrapped = await wrapDekForUser(rawUserKey, perReportDek);

            // Wrap per-report DEK with org public key (org/admin path)
            const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
            const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, perReportDek);

            envelopeFields = {
              user_wrapped_dek: wrapped.user_wrapped_dek,
              user_dek_iv: wrapped.user_dek_iv,
              org_wrapped_dek: bufferToBase64(new Uint8Array(orgWrappedBuf)),
              key_version: orgPublicKey.key_version,
            };
          } else {
            // Legacy: encrypt fields directly with user's key
            encryptedReport = await encryptReportPayload(
              modifiedReports,
              secretKey,
              _encryptData
            );
          }

          let relatedDocumentsEncryptedList;
          if (report?.related_documents) {
            const derivedKey = base64ToUint8Array(secretKey || "");
            relatedDocumentsEncryptedList = await Promise.all(
              report.related_documents.map(async (doc: string) => {
                const normalized = normalizeDocument(doc);
                const hmacHash = await encryptPayloadDeterministic([normalized]);
                const encrypted = await _encryptData(normalized, derivedKey.buffer);

                if (!encrypted.success || !encrypted.data) {
                  throw new Error("Failed to encrypt related document");
                }

                return {
                  related_documents: hmacHash[0],
                  encrypted: encrypted.data.encrypted,
                  iv: encrypted.data.iv,
                };
              })
            );
          }

          // Adicionar report_ids_used sem criptografar
          if (
            report?.report_sources_used &&
            encryptedReport[REPORT_CONSTANTS.new_report.report_sources_used]
          ) {
            const reportIdsUsed = report.report_sources_used.map(
              (source: any) => source.user_reports_id
            );
            const encryptedSourcesUsed = encryptedReport[
              REPORT_CONSTANTS.new_report.report_sources_used
            ] as any;
            encryptedReport[REPORT_CONSTANTS.new_report.report_sources_used] = {
              encrypted: encryptedSourcesUsed.encrypted,
              iv: encryptedSourcesUsed.iv,
              report_ids_used: reportIdsUsed,
            };
          }

          const payload = {
            ...encryptedReport,
            [REPORT_CONSTANTS.new_report.related_documents_encrypted]:
              relatedDocumentsEncryptedList,
            [REPORT_CONSTANTS.new_report.hmac]: {
              [REPORT_CONSTANTS.new_report.ngrams]: ngrams,
            },
            ...(envelopeFields.user_wrapped_dek ? envelopeFields : {}),
          };

          return addNewReport(payload, user_reports_id);
        }
      } catch (error) {
        console.error("Error creating new report:", error);
        throw new Error("Failed to create new report");
      }
    },
  });

  const mergeReportsMutation = useMutation({
    mutationFn: async (payload: MergeReportsProps) => {
      const { reportsSelectedIdList, parent_folder_id, related_documents } = payload;

      if (!reportsSelectedIdList || reportsSelectedIdList.length === 0) {
        throw new Error("No reports selected for merging");
      }

      try {
        const reportsDetails = await Promise.all(
          reportsSelectedIdList.map(async (reportId) => {
            const report = await fetchReportById(reportId);
            if (!report || typeof report !== 'object') {
              throw new Error(`Failed to fetch report with ID: ${reportId}`);
            }
            const reportDecryptor = createEnvelopeAwareDecryptor(report as any);
            return decryptReportPayload(report as ReportData, reportDecryptor);
          })
        ) as ReportData[];

        if (!reportsDetails || reportsDetails.length === 0) {
          throw new Error("Failed to fetch report details");
        }

        const reportSearchArgs = combineReportSearchArgs(reportsDetails);
        const relatedDocumentSearchArgs = {
          [REPORT_CONSTANTS.new_report.related_documents]: related_documents,
        };
        const combinedSearchArgs = {
          ...reportSearchArgs,
          ...relatedDocumentSearchArgs,
        };

        // Combina subject_multiple_p_names e subject_multiple_c_names de todos os relatórios sem duplicatas
        const allMultiplePNames = [...new Set(
          reportsDetails
            .flatMap((r: any) => r?.subject_multiple_p_names || [])
            .filter(Boolean)
        )];
        const allMultipleCNames = [...new Set(
          reportsDetails
            .flatMap((r: any) => r?.subject_multiple_c_names || [])
            .filter(Boolean)
        )];

        const ngrams = await encryptNgrams({
          report_search_args: combinedSearchArgs,
          ...(allMultiplePNames.length && { subject_multiple_p_names: allMultiplePNames }),
          ...(allMultipleCNames.length && { subject_multiple_c_names: allMultipleCNames }),
        });

        const derivedKey = base64ToUint8Array(secretKey || "");
        const encryptedSearchArgs = await _encryptData(combinedSearchArgs, derivedKey.buffer);

        const encryptedRelatedDocuments = await encryptPayloadDeterministic([related_documents]);

        const mergeReportsPayload = {
          [REPORT_CONSTANTS.new_report.report_type]: REPORT_CONSTANTS.types.combinado,
          [REPORT_CONSTANTS.new_report.related_documents]: related_documents,
          [REPORT_CONSTANTS.new_report.report_input_encrypted]: encryptedSearchArgs.data,
          [REPORT_CONSTANTS.new_report.related_documents_encrypted]: encryptedRelatedDocuments[0],
          [REPORT_CONSTANTS.new_report.parent_folder_id]: parent_folder_id,
          [REPORT_CONSTANTS.new_report.hmac]: {
            [REPORT_CONSTANTS.new_report.ngrams]: ngrams,
          },
          [REPORT_CONSTANTS.merge_reports.reports_to_merge]: reportsDetails,
        };

        console.log("[ useReportCRUD ] mergeReportsPayload", mergeReportsPayload);

        return mergeReports(mergeReportsPayload as MergeReportsPayload);
      } catch (error) {
        console.error("Error merging reports:", error);
        throw new Error("Failed to merge reports");
      }
    },
  });

  const deleteReportMutation = useMutation({
    mutationFn: async (reportId: string) => {
      try {
        await deleteReport(reportId);
      } catch (error) {
        console.error("Error deleting report:", error);
        throw error;
      }
    },
    onSuccess: async (data: any) => {
      closeDialog();
      toast.success(`Relatório deletado com sucesso!`);
      queryClient.removeQueries({
        queryKey: ["reports", "mergeable"],
      });
      withPasswordCheck(() => {
        invalidateCurrentFolder(folderId || null);
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar deletar o relatório",
        "Erro ao deletar relatório"
      )(error);
    },
  });

  const invalidateToInitialPage = async () => {
    await resetPagination();
    await queryClient.invalidateQueries({
      queryKey: ["reports", "list", folderId || null],
      exact: true,
    });
  };

  const invalidateReportDetails = async (id: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["reports", "details", id],
      exact: true,
    });
  };

  const setReportDetailsToStale = async (id: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["reports", "details", id],
      exact: true,
      refetchType: "none", // Marca como stale mas não faz refetch imediatamente
    });
  };

  // resetQueries remove os dados do cache forçando refetch na próxima renderização
  const resetReportListQuery = async () => {
    await queryClient.resetQueries({
      queryKey: ["reports", "list"],
      exact: true,
    });
  };

  const invalidateCurrentFolder = (targetFolderId: string | null) => {
    queryClient.invalidateQueries({
      queryKey: ["reports", "list", targetFolderId],
      exact: true,
    });
  };

  const invalidateAllReports = () => {
    queryClient.invalidateQueries({
      queryKey: ["reports", "list"],
      exact: false,
    });
  };

  const invalidateCurrentFolderNoFilters = async (targetFolderId: string | null) => {
    await resetPagination();
    await clearSearchFilter(true);
    await queryClient.invalidateQueries({
      queryKey: ["reports", "list", targetFolderId],
      exact: true,
    });
  };

  const generatePDFMutation = useMutation({
    mutationFn: async (props: ReportPDFProps) => {
      try {
        return await generatePDF(props);
      } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
      }
    },
    onError: (error: Error) => {
      console.error("Error generating PDF:", error);
    },
  });

  const generateDOCXMutation = useMutation({
    mutationFn: async (props: ReportPDFProps) => {
      try {
        return await docxGateway.generateDOCX(props);
      } catch (error) {
        console.error("Error generating DOCX:", error);
        throw error;
      }
    },
    onError: (error: Error) => {
      console.error("Error generating DOCX:", error);
    },
  });

  const renameReportMutation = useMutation({
    mutationFn: async ({ report_id, report_name, user_wrapped_dek, user_dek_iv }: {
      report_id: string;
      report_name: string;
      user_wrapped_dek?: string | null;
      user_dek_iv?: string | null;
    }) => {
      try {
        const hmacResult = await encryptNgrams({
          report_name: report_name,
        });

        let encryptedReportName;

        if (user_wrapped_dek && user_dek_iv) {
          // Envelope path: unwrap the existing per-report DEK and encrypt the new name with it.
          // Other fields (report_status, etc.) share this same DEK, so we must NOT replace it.
          const userDek = base64ToUint8Array(secretKey || "");
          const rawUserKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
          const existingDek = await unwrapDekFromUser(rawUserKey, user_wrapped_dek, user_dek_iv);
          encryptedReportName = await _encryptData(report_name, existingDek.buffer as ArrayBuffer);
        } else {
          // Legacy path: encrypt directly with user's derived key
          const derivedKey = base64ToUint8Array(secretKey || "");
          encryptedReportName = await _encryptData(report_name, derivedKey.buffer);
        }

        if (!encryptedReportName?.success || !encryptedReportName.data) {
          throw new Error("Erro ao criptografar nome do relatório");
        }

        await renameReport({
          [REPORT_CONSTANTS.new_report.report_id]: report_id,
          [REPORT_CONSTANTS.new_report.report_name]: encryptedReportName.data as EncryptedPayload,
          [REPORT_CONSTANTS.new_report.hmac]: {
            [REPORT_CONSTANTS.new_report.ngrams]: hmacResult,
          },
        });
      } catch (error) {
        console.error("Error renaming report:", error);
        throw error;
      }
    },
    onSuccess: async (data: any) => {
      closeDialog();
      toast.success(`Relatório renomeado com sucesso!`);
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(folderId || null);
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar renomear o relatório",
        "Erro ao renomear relatório"
      )(error);
    },
  });

  const batchReportMutation = useMutation({
    mutationFn: async (payload: BatchReportRequest) => {
      try {
        // Generate envelope encryption for the batch header (same pattern as addNewReportMutation)
        // For new batches: generates DEK for the header record
        // For retries: generates DEK so the backend can update the existing header if needed
        if (orgPublicKey && !payload.user_wrapped_dek) {
          const batchHeaderDek = generatePerReportDek();

          const userDek = base64ToUint8Array(secretKey || "");
          const rawUserKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
          const wrapped = await wrapDekForUser(rawUserKey, batchHeaderDek);

          const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
          const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, batchHeaderDek);

          payload = {
            ...payload,
            user_wrapped_dek: wrapped.user_wrapped_dek,
            user_dek_iv: wrapped.user_dek_iv,
            org_wrapped_dek: bufferToBase64(new Uint8Array(orgWrappedBuf)),
            key_version: orgPublicKey.key_version,
          };
        }

        return await createBatchReport(payload);
      } catch (error) {
        console.error("Error creating batch report:", error);
        throw error;
      }
    },
    onSuccess: async (data: any, variables) => {
      closeDialog();
      toast.success(variables.batch_id ? "Refazendo relatórios do lote..." : "Gerando novos relatórios em lote...");
      window.dispatchEvent(new Event("snap:report-created"));
      withPasswordCheck(() => {
        const targetFolder = variables.parent_folder_id ?? null;
        const currentFolder = folderId ?? null;
        invalidateCurrentFolderNoFilters(targetFolder);
        if (targetFolder !== currentFolder) {
          invalidateCurrentFolderNoFilters(currentFolder);
        }
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar criar o lote de relatórios",
        "Erro ao criar lote de relatórios"
      )(error);
    },
  });

  const generateSummaryMutation = useMutation({
    mutationFn: async ({
      report_id,
      data,
      user_prompt,
      ai_model,
      report_sources_data,
      report_sources_used,
    }: {
      report_id: string;
      data: any;
      user_prompt?: string;
      ai_model?: string;
      report_sources_data?: any[];
      report_sources_used?: any[];
    }) => {
      if (!canCreateReportSummary()) {
        throw new Error("Você não tem permissão para gerar sumário executivo");
      }
      try {
        const response = await generateSummaryRequest({
          report_id,
          data,
          ai_model: ai_model ?? getUserAiModel(),
          user_prompt: user_prompt ?? "",
          report_sources_data,
          report_sources_used,
        });
        return { response, report_id };
      } catch (error) {
        console.error("Error generating resume:", error);
        throw error;
      }
    },
    onSuccess: async (data) => {
      toast.success("Gerando sumário executivo...");
      window.dispatchEvent(new Event("snap:report-created"));

      // Atualização otimista: marca o relatório como "gerando sumário"
      const fields = { has_summary: true, summary_generating: true } as any;
      updateItemById(data.report_id, fields);
      queryClient.setQueriesData<any>(
        { queryKey: ["reports", "list"], exact: false },
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item: any) => {
              if (item?.user_reports_id === data.report_id) {
                return { ...item, ...fields };
              }
              return item;
            }),
          };
        }
      );

      withPasswordCheck(() => {
        invalidateSummary(data.report_id);
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar gerar o sumário executivo",
        "Erro ao gerar sumário",
      )(error);
    },
  });

  const updateResumeMutation = useMutation({
    mutationFn: async ({
      reportId,
      summaryPayload,
      report,
    }: {
      reportId: string;
      summaryPayload: SummaryResponse;
      report?: { user_wrapped_dek?: string | null; user_dek_iv?: string | null };
    }) => {
      if (!canCreateReportSummary()) {
        throw new Error("Você não tem permissão para atualizar sumário executivo");
      }
      try {
        let encryptedSummary: ReportData;
        let envelopeFields: { user_wrapped_dek?: string; user_dek_iv?: string; org_wrapped_dek?: string; key_version?: number } = {};

        if (orgPublicKey) {
          const perSummaryDek = generatePerReportDek();
          encryptedSummary = await encryptReportPayload(
            summaryPayload,
            null,
            async (data: any, _ignored: ArrayBuffer) => _encryptData(data, perSummaryDek.buffer as ArrayBuffer),
          );

          const userDek = base64ToUint8Array(secretKey || "");
          const rawUserKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
          const wrapped = await wrapDekForUser(rawUserKey, perSummaryDek);
          const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
          const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, perSummaryDek);

          envelopeFields = {
            user_wrapped_dek: wrapped.user_wrapped_dek,
            user_dek_iv: wrapped.user_dek_iv,
            org_wrapped_dek: bufferToBase64(new Uint8Array(orgWrappedBuf)),
            key_version: orgPublicKey.key_version,
          };
        } else {
          encryptedSummary = await encryptReportPayload(summaryPayload, secretKey, _encryptData);
        }

        if (!encryptedSummary) {
          throw new Error("Erro ao criptografar sumário");
        }
        const payload = { ...encryptedSummary, ...envelopeFields } as unknown as UpdateSummaryInput;
        await updateSummaryRequest(reportId, payload);
      } catch (error) {
        console.error("Error updating resume:", error);
        throw error;
      }
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar salvar o resumo",
        "Erro ao salvar resumo",
      )(error);
    },
  });

  const getSummaryQuery = (reportId: string, report?: { user_wrapped_dek?: string | null; user_dek_iv?: string | null }) =>
    useQuery({
      queryKey: ["reports", "summary", reportId],
      queryFn: async () => {
        try {
          const summaryDTO = await getSummaryRequest(reportId);
          if (!summaryDTO) {
            return null;
          }

          const summaryModel = summaryDTO.getSummaryModel();
          const rawData = summaryModel.raw.data;

          if (
            rawData &&
            typeof rawData === "object" &&
            "encrypted" in rawData
          ) {
            // Use the summary's own envelope fields for decryption
            const summaryDecryptor = createEnvelopeAwareDecryptor(summaryModel.raw as any);
            const decrypted = await summaryDecryptor(rawData);
            if (decrypted.success && decrypted.data) {
              const decryptedResponse = {
                ...summaryModel.raw,
                data: decrypted.data,
              };
              return new SummaryOutputDTO(decryptedResponse);
            }
          }

          return summaryDTO;
        } catch (error: any) {
          // 404 significa que o sumário não existe ainda
          if (error?.response?.status === 404) {
            return null;
          }
          console.error("Error fetching summary:", error);
          throw error;
        }
      },
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!reportId && !isPasswordExpired && canViewReportSummary()
    });

  const invalidateSummary = async (reportId: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["reports", "summary", reportId],
      exact: true,
    });
  };

  return {
    reportListQuery,
    reportDetailsQuery,
    mergeableReportsQuery,
    invalidateToInitialPage,
    invalidateReportDetails,
    setReportDetailsToStale,
    resetReportListQuery,
    invalidateCurrentFolder,
    invalidateAllReports,
    invalidateCurrentFolderNoFilters,
    newReportMutation,
    addNewReportMutation,
    deleteReportMutation,
    generatePDFMutation,
    generateDOCXMutation,
    renameReportMutation,
    mergeReportsMutation,
    batchReportMutation,
    generateSummaryMutation,
    updateResumeMutation,
    getSummaryQuery,
    invalidateSummary,
  };
};
